import { ShoppingCart, Lock, CheckCircle, Loader } from 'lucide-react';
import { Card, Button } from '../common';
import { useSpreads, useLastSpreadKey, useSaveLastSpreadKey } from '../../hooks/useSpreads';
import type { SpreadType } from '../../api/spreads';

interface SpreadSelectorV2Props {
  /** Called when user confirms a spread selection */
  onSelectSpread: (spread: SpreadType) => void;
  /** Currently selected spread key (controlled) */
  selectedSpreadKey?: string;
}

const DIFFICULTY_STYLES: Record<string, string> = {
  beginner: 'text-green-400 bg-green-400/10',
  intermediate: 'text-yellow-400 bg-yellow-400/10',
  advanced: 'text-red-400 bg-red-400/10',
};

export function SpreadSelectorV2({ onSelectSpread, selectedSpreadKey }: SpreadSelectorV2Props) {
  const { data, isLoading, isError } = useSpreads();
  const lastSpreadKey = useLastSpreadKey();
  const saveLastSpreadKey = useSaveLastSpreadKey();

  const spreads = data?.data?.spreads ?? [];

  // Resolve the active key: prop > user preference > localStorage fallback
  // This is used for visual highlighting only, not for auto-selection
  const activeKey =
    selectedSpreadKey ??
    lastSpreadKey ??
    localStorage.getItem('lastSpreadKey') ??
    spreads[0]?.spreadKey;

  const handleSelect = (spread: SpreadType) => {
    if (!spread.isAccessible) return;
    saveLastSpreadKey(spread.spreadKey);
    onSelectSpread(spread);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-4">
        <Loader className="h-8 w-8 text-primary-500 animate-spin" />
        <p className="text-gray-400">Loading spreads...</p>
      </div>
    );
  }

  if (isError || spreads.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">Unable to load spreads. Please try again.</p>
      </div>
    );
  }

  const selectedSpread = spreads.find(s => s.spreadKey === activeKey);

  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">Choose Your Spread</h2>
        <p className="text-gray-400">Select a tarot spread that resonates with your question</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {spreads.map(spread => {
          const isSelected = spread.spreadKey === activeKey;
          const locked = !spread.isAccessible;

          return (
            <Card
              key={spread.spreadKey}
              className={`
                transition-all duration-200
                ${locked
                  ? 'opacity-70 cursor-not-allowed'
                  : 'cursor-pointer hover:shadow-lg hover:border-primary-500/50'}
                ${isSelected ? 'ring-2 ring-primary-500 bg-primary-500/10' : ''}
              `}
              onClick={() => handleSelect(spread)}
            >
              <div className="space-y-3">
                {/* Header */}
                <div className="flex justify-between items-start">
                  <h3 className="text-lg font-semibold text-white">{spread.nameEn}</h3>
                  <div className="flex flex-col items-end space-y-1">
                    <span className={`text-xs px-2 py-1 rounded ${DIFFICULTY_STYLES[spread.difficulty] ?? 'text-gray-400 bg-gray-400/10'}`}>
                      {spread.difficulty}
                    </span>
                  </div>
                </div>

                {/* Description */}
                <p className="text-gray-400 text-sm">{spread.descriptionTh || spread.nameEn}</p>

                {/* Card count */}
                <div className="flex items-center justify-between">
                  <span className="text-primary-500 font-semibold text-sm">
                    {spread.cardCount} {spread.cardCount === 1 ? 'Card' : 'Cards'}
                  </span>
                  {isSelected && !locked && (
                    <CheckCircle className="h-4 w-4 text-primary-500" />
                  )}
                </div>

                {/* Purchase badge for celtic_10 */}
                {spread.requiresPurchase && (
                  <div className={`flex items-center space-x-2 text-xs px-2 py-1.5 rounded ${
                    spread.isPurchased
                      ? 'bg-green-500/10 text-green-400'
                      : 'bg-yellow-500/10 text-yellow-400'
                  }`}>
                    {spread.isPurchased ? (
                      <>
                        <CheckCircle className="h-3.5 w-3.5 flex-shrink-0" />
                        <span>Purchased</span>
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="h-3.5 w-3.5 flex-shrink-0" />
                        <span>{spread.coinCost ?? spread.purchaseItem?.priceCoins ?? '?'} coins to unlock</span>
                      </>
                    )}
                  </div>
                )}

                {/* Locked overlay hint */}
                {locked && (
                  <div className="flex items-center space-x-1 text-xs text-gray-500">
                    <Lock className="h-3.5 w-3.5" />
                    <span>Purchase required</span>
                  </div>
                )}

                {/* Visual layout preview */}
                <div className="relative h-14 bg-dark-700 rounded overflow-hidden">
                  {spread.positions.slice(0, Math.min(5, spread.cardCount)).map(pos => (
                    <div
                      key={pos.id}
                      className="absolute w-3 h-5 bg-primary-500/50 rounded-sm"
                      style={{ left: `${pos.x}%`, top: `${pos.y}%`, transform: 'translate(-50%, -50%)' }}
                    />
                  ))}
                  {spread.cardCount > 5 && (
                    <div className="absolute bottom-1 right-1 text-xs text-gray-500">
                      +{spread.cardCount - 5} more
                    </div>
                  )}
                </div>

                <Button
                  variant={isSelected ? 'primary' : 'secondary'}
                  size="sm"
                  fullWidth
                  disabled={locked}
                  onClick={e => { e.stopPropagation(); handleSelect(spread); }}
                >
                  {locked ? 'Locked' : isSelected ? 'Selected' : 'Choose This Spread'}
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Selected spread summary */}
      {selectedSpread && (!selectedSpread.requiresPurchase || selectedSpread.isPurchased) ? (
        <Card className="bg-primary-500/10 border border-primary-500/20">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-white mb-1">{selectedSpread?.nameEn} Selected</h3>
            <p className="text-gray-300 text-sm mb-3">{selectedSpread?.descriptionTh}</p>
            <div className="flex justify-center space-x-4 text-sm text-primary-400">
              <span>📊 {selectedSpread?.cardCount} cards</span>
              <span>🎯 {selectedSpread?.difficulty} level</span>
            </div>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
